/**
 * AntragSignaturDialog - «Zur Unterschrift senden» (Tab 5a, P2 Slice 13). Radio for the channel
 * (portal / email / esign), preselected from kanal_vorschlag, plus the Frist. The server owns the gates.
 */

import { useState } from 'react';
import {
  ANTRAG_ESIGN_NICHT_VERFUEGBAR_HINWEIS,
  ANTRAG_ESIGN_NOT_WIRED_HINWEIS,
  ANTRAG_FORMULAR_DOCX_HINWEIS,
  ANTRAG_KEINE_EMAIL_HINWEIS,
  ANTRAG_KEIN_PORTAL_HINWEIS,
  ANTRAG_SIGNATUR_EMAIL_HINWEIS,
  ANTRAG_SIGNATUR_PORTAL_HINWEIS,
  type AntragResponse,
  type AntragSignaturBody,
  type SignatureChannel,
} from '@brokly/shared';
import { Dialog } from '../administration/components';
import { Button } from '../../components/ui/Button';

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const ERROR_TEXT: Record<string, string> = {
  versand_unterbrochen: 'Versand unterbrochen - bitte zuerst aufloesen',
  portal_zugang_fehlt: ANTRAG_KEIN_PORTAL_HINWEIS,
  kunde_email_fehlt: ANTRAG_KEINE_EMAIL_HINWEIS,
  esign_not_wired: ANTRAG_ESIGN_NOT_WIRED_HINWEIS,
  esign_nicht_verfuegbar: ANTRAG_ESIGN_NICHT_VERFUEGBAR_HINWEIS,
  frist_invalid: 'Die Frist muss in der Zukunft liegen',
  mailbox_not_connected: 'Keine Mailbox verbunden - bitte zuerst unter Einstellungen verbinden',
  communication_send_required: 'Keine Berechtigung zum Versand von E-Mails',
  versand_laeuft: 'Versand laeuft - bitte warten',
};

export function AntragSignaturDialog({
  open,
  onClose,
  facts,
  onSenden,
  saving,
  error,
}: {
  open: boolean;
  onClose: () => void;
  facts: AntragResponse;
  onSenden: (body: AntragSignaturBody) => void;
  saving: boolean;
  error: { code?: string } | null;
}) {
  const [channel, setChannel] = useState<SignatureChannel>(facts.kanal_vorschlag);
  const [frist, setFrist] = useState(facts.frist_vorschlag);
  const errorText = error?.code ? (ERROR_TEXT[error.code] ?? 'Zustellung fehlgeschlagen') : null;
  const isDocx = facts.antrag?.doc.mime === DOCX_MIME;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Zur Unterschrift senden"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button
            disabled={
              saving ||
              (channel === 'portal' && !facts.portal_verfuegbar) ||
              (channel === 'email' && facts.kunde_email === null) ||
              (channel === 'esign' && !facts.esign_verfuegbar)
            }
            onClick={() => onSenden({ channel, frist_date: frist || undefined })}
            data-testid="antrag-signatur-submit"
          >
            Zur Unterschrift senden
          </Button>
        </div>
      }
    >
      <div className="space-y-3 text-sm" data-testid="antrag-signatur-dialog">
        <fieldset className="space-y-2">
          <label className="flex items-start gap-2">
            <input type="radio" name="antrag-channel" value="portal" checked={channel === 'portal'} disabled={!facts.portal_verfuegbar} onChange={() => setChannel('portal')} data-testid="antrag-channel-portal" />
            <span>
              Kundenportal
              {!facts.portal_verfuegbar && <span className="block text-xs text-muted-foreground">{ANTRAG_KEIN_PORTAL_HINWEIS}</span>}
              {facts.portal_verfuegbar && <span className="block text-xs text-muted-foreground">{ANTRAG_SIGNATUR_PORTAL_HINWEIS}</span>}
            </span>
          </label>
          <label className="flex items-start gap-2">
            <input type="radio" name="antrag-channel" value="email" checked={channel === 'email'} disabled={facts.kunde_email === null} onChange={() => setChannel('email')} data-testid="antrag-channel-email" />
            <span>
              E-Mail an {facts.kunde_email ?? '-'}
              {facts.kunde_email === null && <span className="block text-xs text-muted-foreground">{ANTRAG_KEINE_EMAIL_HINWEIS}</span>}
              {facts.kunde_email !== null && <span className="block text-xs text-muted-foreground">{ANTRAG_SIGNATUR_EMAIL_HINWEIS}</span>}
            </span>
          </label>
          <label className="flex items-start gap-2">
            <input type="radio" name="antrag-channel" value="esign" checked={channel === 'esign'} disabled={!facts.esign_verfuegbar} onChange={() => setChannel('esign')} data-testid="antrag-channel-esign" />
            <span>
              Elektronische Signatur (FES)
              {!facts.esign_verfuegbar && <span className="block text-xs text-muted-foreground">{ANTRAG_ESIGN_NICHT_VERFUEGBAR_HINWEIS}</span>}
            </span>
          </label>
        </fieldset>
        <label className="block">
          <span className="text-xs text-muted-foreground">Frist fuer die Unterschrift</span>
          <input type="date" value={frist} onChange={(e) => setFrist(e.target.value)} className="mt-1 w-full rounded-md border border-border p-2" data-testid="antrag-frist-input" />
        </label>
        {isDocx && <p role="note" className="text-xs text-muted-foreground">{ANTRAG_FORMULAR_DOCX_HINWEIS}</p>}
        {errorText && <p className="text-sm text-status-danger" data-testid="antrag-signatur-error">{errorText}</p>}
      </div>
    </Dialog>
  );
}
